import { Fuel, Cog, ScanLine, AlertTriangle, Zap, Cog as Engine, ArrowLeftRight, Car, Disc, Snowflake, Gauge, Truck, Cpu, KeyRound, ClipboardCheck, Droplets } from "lucide-react";
import { Reveal } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const SERVICES = [
  { icon: Fuel, title: "Diesel Repair", desc: "Full-service diesel engine diagnostics and repair for pickups, work trucks and duallys.", span: "sm:col-span-2" },
  { icon: Engine, title: "Gasoline Engine Repair", desc: "Complete repair for gas engines of all makes and models." },
  { icon: ScanLine, title: "Computer Diagnostics", desc: "Dealer-level scan tools pinpoint the real cause before any part is replaced.", span: "sm:col-span-2" },
  { icon: AlertTriangle, title: "Check Engine Light Diagnostics", desc: "Accurate code analysis and root-cause testing — not guesswork." },
  { icon: Zap, title: "Electrical Diagnostics & Repair", desc: "Wiring, modules, sensors and charging systems traced and repaired." },
  { icon: Cog, title: "Engine Repair & Replacement", desc: "From gaskets to full engine replacement, done to spec." },
  { icon: ArrowLeftRight, title: "Transmission Service & Replacement", desc: "Service, diagnostics and replacement for automatic transmissions." },
  { icon: Car, title: "Suspension & Steering", desc: "Shocks, struts, ball joints, tie rods and alignments-related repair." },
  { icon: Disc, title: "Brake Repair", desc: "Pads, rotors, calipers, hydraulics and ABS brake systems." },
  { icon: Snowflake, title: "A/C Service & Repair", desc: "Recharge, leak detection, compressors and full A/C system repair." },
  { icon: Gauge, title: "Diesel Fuel Systems", desc: "Injectors, pumps, rails and high-pressure fuel system service.", span: "sm:col-span-2" },
  { icon: Truck, title: "Work & Fleet Pickup Repair", desc: "Service for work pickups and small fleets that keeps your trucks earning.", span: "sm:col-span-2" },
  { icon: Cpu, title: "Vehicle Programming & Module Programming", desc: "Module programming, coding and relearn procedures." },
  { icon: KeyRound, title: "Key / Immobilizer Programming", desc: "Key, fob and immobilizer programming for most vehicles." },
  { icon: ClipboardCheck, title: "Preventive Maintenance", desc: "Scheduled service that prevents breakdowns before they happen." },
  { icon: Droplets, title: "Oil & Filter Service", desc: "Fast, thorough oil and filter service for gas and diesel." },
];

export const Services = () => (
  <section id="services" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#0A0A0A]" data-testid="services-section">
    <Reveal>
      <p className="text-[#FF3B30] text-xs uppercase tracking-[0.35em] font-semibold mb-4">01 — What We Fix</p>
      <h2 className="font-oswald font-bold uppercase tracking-tight text-4xl sm:text-5xl max-w-3xl leading-[1.02]" data-testid="services-heading">
        Full-Service Repair. <span className="text-zinc-500">Gas &amp; Diesel.</span>
      </h2>
      <p className="mt-5 max-w-2xl text-zinc-400 text-base md:text-lg">
        From a check engine light to a complete engine or transmission replacement — we diagnose first, then fix it right.
      </p>
    </Reveal>

    <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 grid-flow-dense">
      {SERVICES.map(({ icon: Icon, title, desc, span }, i) => (
        <Reveal key={title} delay={(i % 4) * 0.06} className={span || ""}>
          <button
            onClick={() => scrollToId("request")}
            data-testid={`service-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className="group w-full h-full text-left bg-zinc-900/60 border border-zinc-800 p-6 hover:border-[#FF3B30] transition-colors duration-300 flex flex-col focus-visible:ring-2 focus-visible:ring-[#FF3B30] focus-visible:outline-none"
          >
            <div className="flex items-start justify-between">
              <Icon className="w-6 h-6 text-[#FF3B30]" strokeWidth={1.5} />
              <span className="font-oswald text-zinc-700 text-sm group-hover:text-[#FF3B30] transition-colors duration-300">
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>
            <h3 className="mt-6 font-oswald uppercase tracking-tight text-xl leading-tight">{title}</h3>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{desc}</p>
            <span className="mt-auto pt-5 text-xs uppercase tracking-[0.2em] text-zinc-600 group-hover:text-[#FF3B30] transition-colors duration-300">
              Request this service →
            </span>
          </button>
        </Reveal>
      ))}
    </div>
  </section>
);
